<template>
  <spinner v-if="loading" />
  <div v-else-if="stage === 1" class="container-positions">
    <div v-for="(moneyLineGroup, aIx) in groupedMoneyLines" :key="aIx">
      <div class="box-position">
        <!-- Title of box (mySide) -->
        <my-position-header
          :game="game"
          :obj-side="moneyLineGroup"
          :type-bet="'moneyline'"
          :key-obj="aIx"
          @change-accordeon-opened="changeAccordeonOpened"
        />
        <div v-if="moneyLineGroup.expanded">
          <div v-if="moneyLineGroup.away">
            <my-position-accordeon
              v-if="moneyLineGroup.away.bets.length > 1"
              :obj-side="moneyLineGroup.away"
              :league="league"
              @display-bet="displayBet"
            />
            <my-position-accordeon-item
              v-else
              :obj-side="moneyLineGroup.away"
              :bet="moneyLineGroup.away.bets[0]"
              :league="league"
              @display-bet="displayBet"
            />
          </div>

          <div v-if="moneyLineGroup.home">
            <my-position-accordeon
              v-if="moneyLineGroup.home.bets.length > 1"
              :obj-side="moneyLineGroup.home"
              :league="league"
              @display-bet="displayBet"
            />
            <my-position-accordeon-item
              v-else
              :obj-side="moneyLineGroup.home"
              :bet="moneyLineGroup.home.bets[0]"
              :league="league"
              @display-bet="displayBet"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="stage === 2">
    <button class="button-back mb-1" @click="stage = 1">
      ⇠ Return To Summary
    </button>
    <double-bet-info-modal :bet="session" />
  </div>
</template>

<script setup>
//CORE
import {
  calculateWin,
  getSideString,
  signOdds,
  fixed2,
  calculateAverageOdds,
} from '@/core/utils';
import {
  commissionAdjustedRisk,
  commissionAdjustedWin,
} from '@/core/commissionUtils';
import { useUserStore } from '@/stores/user';
import { useMiscStore } from '@/stores/store';
import { ref, onMounted } from 'vue';
import { emitter } from '../../../core/emitter';

//COMPONENTS
import MyPositionAccordeon from '@/components/userBets/MyPositionAccordeon.vue';
import MyPositionAccordeonItem from '@/components/userBets/MyPositionSingleBet.vue';
import MyPositionHeader from '@/components/userBets/myPosition/MyPositionHeader.vue';
import Spinner from '@/components/global/Spinner.vue';
import DoubleBetInfoModal from '@/components/userBets/modals/DoubleBetInfoModal.vue';

//PROPS
const props = defineProps({
  game: {
    type: Object,
    default: null,
  },
  moneyLineAway: {
    type: Array,
    required: true,
  },
  moneyLineHome: {
    type: Array,
    required: true,
  },
  moneyLineHomeRiskWin: {
    type: Object,
    required: true,
  },
  avgOddsMoneyLine: {
    type: Number,
    required: true,
  },
  moneyLineAwayRiskWin: {
    type: Object,
    required: true,
  },
  moneylineRisk: {
    type: Object,
    required: true,
  },
  groupedBets: {
    type: Object,
    required: true,
  },
  league: {
    type: String,
    default: '',
  },
});

//CONST
const userStore = useUserStore();
const store = useMiscStore();
const stage = ref(1);
const session = ref({});
const away = ref([]);
const home = ref([]);
const dataAway = ref(false);
const dataHome = ref(false);
const groupedMoneyLines = ref(null);
const myMoneyLineAway = ref('');
const myMoneyLineHome = ref('');
const moneyLineNameAway = ref('');
const moneyLineNameHome = ref('');
const awayRiskWin = ref([]);
const homeRiskWin = ref([]);
const loading = ref(true);

//HOOK
onMounted(() => {
  away.value = [Object.assign({}, props.moneyLineAway)];
  away.value = Object.assign([], away.value);
  home.value = [Object.assign({}, props.moneyLineHome)];
  home.value = Object.assign([], home.value);
  myMoneyLineAway.value = Object.keys(away.value[0]);
  myMoneyLineHome.value = Object.keys(home.value[0]);

  if (myMoneyLineAway.value[0]) {
    moneyLineNameAway.value = away.value[0][0].game.participants[0].longName;
    awayRiskWin.value = props.moneyLineAwayRiskWin;
    dataAway.value = true;
  }
  if (myMoneyLineHome.value[0]) {
    moneyLineNameHome.value = home.value[0][0].game.participants[1].longName;
    homeRiskWin.value = props.moneyLineHomeRiskWin;
    dataHome.value = true;
  }
  loading.value = false;

  groupByMoneyLine();
});

//FUNCTIONS
function getBestSideBet(homeObj, awayObj) {
  const accumHomeRisk =
    homeObj.sumRiskBets !== undefined ? parseFloat(homeObj.sumRiskBets) : 0;
  const accumHomeWin =
    homeObj.sumWinBets !== undefined ? parseFloat(homeObj.sumWinBets) : 0;
  const accumAwayRisk =
    awayObj.sumRiskBets !== undefined ? parseFloat(awayObj.sumRiskBets) : 0;
  const accumAwayWin =
    awayObj.sumWinBets !== undefined ? parseFloat(awayObj.sumWinBets) : 0;

  const winAwayMinusRiskHome = accumAwayWin - accumHomeRisk;
  const winHomeMinusRiskAway = accumHomeWin - accumAwayRisk;

  const objBigWin =
    winHomeMinusRiskAway > winAwayMinusRiskHome ? homeObj : awayObj;

  let calculatedRisk;
  let calculatedWin;

  if (
    (accumHomeRisk === 0 && accumHomeWin === 0) ||
    (accumAwayRisk === 0 && accumAwayWin === 0)
  ) {
    calculatedRisk = accumHomeRisk !== 0 ? accumHomeRisk : accumAwayRisk;
    calculatedWin = accumHomeWin !== 0 ? accumHomeWin : accumAwayWin;
  } else if (accumAwayWin - accumHomeRisk > accumHomeWin - accumAwayRisk) {
    calculatedRisk = accumAwayRisk - accumHomeWin;
    calculatedWin = accumAwayWin - accumHomeRisk;
  } else if (accumHomeWin - accumAwayRisk > accumAwayWin - accumHomeRisk) {
    calculatedRisk = accumHomeRisk - accumAwayWin;
    calculatedWin = accumHomeWin - accumAwayRisk;
  }

  const risk = fixed2(calculatedRisk);
  const win = fixed2(calculatedWin);
  const odds = calculateAvgOddsMoneyLines(calculatedRisk, calculatedWin);
  return {
    moneyLineName: objBigWin.participantInfo.longName,
    moneyLine: objBigWin.realMoneyLine,
    odds,
    risk,
    win,
  };
}

function groupByMoneyLine() {
  if (!dataAway.value && !dataHome.value) {
    return false;
  }

  const grouped = {
    0: {
      home: false,
      away: false,
      mySide: false,
      expanded: true,
    },
  };

  if (props.groupedBets.moneyline.home) {
    const { sumRiskBets, sumWinBets, participantInfo, gameObj } =
      reduceRiskWinByBet(props.groupedBets.moneyline.home);
    const objHome = {
      bets: props.groupedBets.moneyline.home,
      sumRiskBets,
      sumWinBets,
      realMoneyLine: 0,
      participantInfo,
      gameObj,
      avgOdds: calculateAvgOddsMoneyLines(sumRiskBets, sumWinBets),
    };

    grouped[0].home = objHome;
  }

  if (props.groupedBets.moneyline.away) {
    const { sumRiskBets, sumWinBets, participantInfo, gameObj } =
      reduceRiskWinByBet(props.groupedBets.moneyline.away);
    const objAway = {
      bets: props.groupedBets.moneyline.away,
      sumRiskBets,
      sumWinBets,
      realMoneyLine: 0,
      participantInfo,
      gameObj,
      avgOdds: calculateAvgOddsMoneyLines(sumRiskBets, sumWinBets),
    };

    grouped[0].away = objAway;
  }

  grouped[0].mySide = getBestSideBet(grouped[0].home, grouped[0].away);
  return (groupedMoneyLines.value = grouped);
}

function calculateAvgOddsMoneyLines(risk, win) {
  const avgOdds = calculateAverageOdds(risk, win);
  const avgOddsABS =
    Number.isNaN(avgOdds) || Math.abs(avgOdds) === Infinity ? 0 : avgOdds;
  return avgOddsABS > -100 && avgOddsABS < 100 ? 0 : fixed2(avgOddsABS);
}

function reduceRiskWinByBet(bets) {
  let sumRiskBets = 0;
  let sumWinBets = 0;
  let participantInfo;
  let gameObj;

  if (userStore.user.viewOddsWithCommission) {
    bets.forEach((bet) => {
      const betRisk =
        bet.origin === 'offer'
          ? bet.bet
          : commissionAdjustedRisk(
              bet.bet,
              bet.odds,
              bet.game.league.toLowerCase(),
              'moneyline',
              userStore,
            );
      const tempWin = calculateWin(bet.bet, bet.odds);
      const betWin =
        bet.origin === 'offer'
          ? tempWin
          : commissionAdjustedWin(
              tempWin,
              bet.game.league.toLowerCase(),
              'moneyline',
              userStore,
            );
      sumRiskBets += betRisk;
      sumWinBets += betWin;
      participantInfo = bet.game.participants.find(
        (p) => p.id === bet.participantID,
      );
      gameObj = bet.game;
    });
  } else {
    bets.forEach((bet) => {
      sumRiskBets += bet.bet;
      sumWinBets += calculateWin(bet.bet, bet.odds);
      participantInfo = bet.game.participants.find(
        (p) => p.id === bet.participantID,
      );
      gameObj = bet.game;
    });
  }
  return {
    sumRiskBets: fixed2(sumRiskBets),
    sumWinBets: fixed2(sumWinBets),
    participantInfo,
    gameObj,
  };
}

function changeAccordeonOpened(key) {
  groupedMoneyLines.value[key].expanded =
    !groupedMoneyLines.value[key].expanded;
}

//EMITTERS
//local
const emit = defineEmits(['close-modal']);

function close() {
  emit('close-modal');
}
//global
function displayBet(moneyLine) {
  session.value = moneyLine;
  emitter.emit('displayBet', session.value);
  loading.value = false;
  stage.value = 2;
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/tokens.scss' as main;

.container-positions {
  padding-left: 20px;
  padding-right: 20px;
}

.box-position {
  border-radius: 8px;
  background: main.$white_01;
  margin: 0;
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: 16px;
}

.title-position {
  background: main.$blue-702;
  color: main.$white;
  font-size: small;
  font-weight: bold;
  padding: 8px;
  margin: 0;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
  cursor: pointer;
}

.spacing-vertical-1 {
  height: 12px;
}

.betWin {
  background: main.$green-402;
}

.betLoss {
  background: main.$red-101;
}

.betDraw {
  background: main.$yellow-090;
}

//.betHalfWin
//   background: lightgreen

.betNormal {
  background: main.$black;
}

.bet {
  color: main.$blue-1100;
  font-size: small;
  margin: 0;
  width: 100%;
  border-top: 1px solid main.$grey-1000;
  background: main.$white;

  &-main-info {
    text-transform: uppercase;
    font-weight: main.$font-weight-bold;
    color: main.$color-primary;
    font-size: main.$font-size-sm;
  }

  &-risk {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    font-weight: main.$font-weight-bold;
  }

  &-type {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    font-weight: main.$font-weight-bold;
  }

  &-plus {
    font-size: main.$font-size-sm;
    color: main.$white;
    cursor: pointer;
    font-weight: main.$font-weight-bold;
  }

  &-time {
    font-size: x-small;
    color: main.$blue-1100;
    font-weight: main.$font-weight-normal;
    text-transform: uppercase;
  }

  &-teams {
    font-size: x-small;

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      display: none;
    }
  }

  &-small {
    font-size: x-small;
    text-transform: uppercase;
  }
}

.win,
.lose,
.cancelled,
.refunded {
  margin: 0;
  padding: 0.35rem 2rem;
  display: inline-block;
  font-family: inherit;
  border-radius: 4px;
  font-size: main.$font-size-xs;
  color: main.$white;
}

.win {
  background: main.$green-500;
}

.lose {
  background: main.$red-500;
}

.smallText {
  display: none;
}

.cancelled,
.refunded {
  background: main.$grey-1701;
}

.bar-separate {
  text-align: center;
  font-size: 12px;
  color: main.$blue-201;
}

.button-back {
  width: 100%;
  background: main.$grey-500;
  color: main.$blue-1100;
  font-weight: bold;
}

svg[data-icon='chevron-up'],
svg[data-icon='chevron-down'] {
  color: main.$white;
}

svg[data-icon='plus'] {
  padding: 2px;
  font-size: 12px;
  background: main.$blue-404;
  border-radius: 360px;
}

svg[data-icon='plus']:hover {
  opacity: 0.9;
}

.short-info {
  font-size: small;
  font-weight: bold;
  color: main.$blue-702;
  text-align: center;
}

.jump {
  min-width: 10%;
}

.iconAccordeon {
  color: main.$color-primary;
}
</style>
